import React, { useEffect, useState } from 'react'
import { getSessionStorage } from '../../../utils/sessionStorage'
import refineSearchAndNavigateToBrowsePageWithQueryParams from '../../../domain/search/refineSearchAndNavigateToBrowsePageWithQueryParams'
import { isNonEmptyArray, isNonEmptyString } from '../../../utils/checks'
import { useLocation } from 'react-use'
import clearAllRefinementsFromSearchState from '../../../domain/search/clearAllRefinementsFromSearchState'
import { navigateToBrowsePageWithQueryParams } from '../../../domain/search/navigateToBrowsePageWithQueryParams'
import refineSearchStateByRemovingLocationsAndGeoLoc from '../../../domain/search/refineSearchStateByRemovingLocationsAndGeoLoc'

import FilterPill from '../../atoms/FilterPill/FilterPill'
import IconRemove from '@/components/icons/IconRemove/IconRemove'
import { SEARCH_AROUND_ME_LOCATION_NAME } from '../../../domain/browseBy/constants'
import { getDisplayLabel } from '@/domain/filterOptions/useCases/getDisplayLabel'

const showClearAllFilters = ({
  filtersFromSearchState,
  showFromSearchFilter
}) => {
  return (
    filtersFromSearchState.length > 1 ||
    (filtersFromSearchState.length === 1 &&
      isNonEmptyString(showFromSearchFilter))
  )
}

const getDisplayLabelForFilter = (filter) => {
  const itemLike = { label: filter.label }
  return getDisplayLabel(itemLike, filter.type)
}

const FilterPillList = ({
  onHasFilter,
  className = '',
  fromFacetOrLocationPage = false,
  isAroundLatLngViaIP = false
}) => {
  const [filtersFromSearchState, setFiltersFromSearchState] = useState([])
  const [showFromSearchFilter, setShowFromSearchFilter] = useState('')

  const location = useLocation()
  useEffect(() => {
    const searchState = getSessionStorage('searchState') || {}

    const filters = Object.entries(searchState?.refinementList || {})
      .map((entry) => {
        const type = entry[0]
        const values = isNonEmptyArray(entry[1]) ? entry[1] : [entry[1]]
        return values.map((label) => ({ type, label }))
      })
      .flat()

    const isAroundLatLngViaIPFilter = searchState.configure?.aroundLatLngViaIP

    const nearMeFilter = isAroundLatLngViaIPFilter
      ? { type: 'locations', label: SEARCH_AROUND_ME_LOCATION_NAME }
      : null

    const existingLocationFilter = filters.find(
      (filter) => filter.type === 'locations'
    )
    const otherFilters = filters.filter((filter) => filter.type !== 'locations')

    let combinedFilters = []

    if (nearMeFilter) {
      combinedFilters.push(nearMeFilter)
    } else if (existingLocationFilter) {
      combinedFilters.push(existingLocationFilter)
    }

    combinedFilters = [...combinedFilters, ...otherFilters]

    const showFromSearchFilter =
      !searchState?.refinementList?.locations &&
      !isAroundLatLngViaIPFilter &&
      searchState?.configure?.aroundLatLng &&
      searchState?.locationName

    setShowFromSearchFilter(showFromSearchFilter)
    setFiltersFromSearchState(combinedFilters)
    onHasFilter(
      isNonEmptyArray(combinedFilters) || isNonEmptyString(showFromSearchFilter)
    )
  }, [location])

  const handleClearAllFilters = () => {
    const clearedSearchState = clearAllRefinementsFromSearchState()
    navigateToBrowsePageWithQueryParams(clearedSearchState)
  }

  const handleRemoveFilter = (filter) => {
    if (filter.type === 'locations') {
      const refinedSearchState = refineSearchStateByRemovingLocationsAndGeoLoc()
      navigateToBrowsePageWithQueryParams(refinedSearchState)
      return
    }

    refineSearchAndNavigateToBrowsePageWithQueryParams(
      {
        ...filter,
        remove: true
      },
      fromFacetOrLocationPage
    )
  }

  if (
    isNonEmptyArray(filtersFromSearchState) ||
    isNonEmptyString(showFromSearchFilter)
  ) {
    return (
      <div className={`flex flex-wrap ${className}`}>
        {isNonEmptyString(showFromSearchFilter) && (
          <FilterPill
            className="mb-2 mr-2 !pr-2"
            filter={{ type: 'locations' }}
            onClick={handleRemoveFilter}
          >
            <div className="flex items-center justify-center gap-1">
              <div>{showFromSearchFilter}</div>
              <IconRemove className="size-4" />
            </div>
          </FilterPill>
        )}
        {filtersFromSearchState.map((filter) => (
          <FilterPill
            className="mb-2 mr-2 !pr-2"
            key={filter.label}
            filter={filter}
            onClick={handleRemoveFilter}
          >
            <div className="flex items-center justify-center gap-1">
              <div>{getDisplayLabelForFilter(filter)}</div>
              <IconRemove className="size-4" />
            </div>
          </FilterPill>
        ))}
        {showClearAllFilters({
          filtersFromSearchState,
          showFromSearchFilter
        }) && (
          <FilterPill
            className="mb-2 mr-2 !bg-gray-200 hover:!bg-gray-300 active:!bg-gray-400"
            onClick={handleClearAllFilters}
          >
            Clear All
          </FilterPill>
        )}
      </div>
    )
  }

  return null
}

export default FilterPillList
